import React, { useCallback } from 'react'
import type { PhotoCandid } from '@/api/types/photo'
import { TrayThumbnailCard } from '@/components/app/panels/right-panel/tray/tray-thumbnail-card'
import { usePhotoPlacementOptional } from '@/contexts/photo-placement-context'
import { PHOTO_TRAY_DRAGGING_THUMB_OPACITY } from '@/lib/photos/photo-placement-config'
import { startTrayDragFromPointer } from '@/lib/placement/start-tray-drag-from-pointer'
import { resolveCanvasPhotoUrl } from '@/lib/photos/resolve-canvas-photo-url'

type PhotoTrayThumbnailProps = {
  photo: PhotoCandid
  showFileName?: boolean
  showRemove?: boolean
  onRemove?: () => void
  removeDisabled?: boolean
  /** 1 = large single-column card; 2 = compact two-column grid (default). */
  columns?: 1 | 2
  onPreviewOpenChange?: (open: boolean) => void
}

/**
 * Photos-tray thumbnail: the shared `TrayThumbnailCard`, wired to photo placement — pointer
 * down starts a drag onto the canvas, Enter/Space starts a keyboard carry.
 */
export function PhotoTrayThumbnail({
  photo,
  showFileName = false,
  showRemove = false,
  onRemove,
  removeDisabled = false,
  columns = 2,
  onPreviewOpenChange,
}: PhotoTrayThumbnailProps) {
  const placement = usePhotoPlacementOptional()
  const isDraggingThis = placement?.draggingCandidId === photo.candidId && placement.isDraggingPhoto

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>, card: HTMLDivElement) => {
      if (!placement) return

      startTrayDragFromPointer(e, card, (clientX, clientY, rect, pointerId, pointerType, el) =>
        placement.beginPointerDrag(photo, clientX, clientY, rect, pointerId, pointerType, el)
      )
    },
    [photo, placement]
  )

  const handleActivate = useCallback(
    (rect: DOMRect) => placement?.beginKeyboardCarry(photo, rect),
    [photo, placement]
  )

  return (
    <TrayThumbnailCard
      itemKey={photo.candidId}
      label={photo.displayName}
      ariaLabel={`${photo.displayName}. Press Enter or Space to pick up for placement on canvas.`}
      imageUrl={photo.urlSqCrop}
      previewUrl={resolveCanvasPhotoUrl(photo) || photo.urlSqCrop}
      showCaption={showFileName}
      placed={photo.placedFlag}
      columns={columns}
      opacity={isDraggingThis ? PHOTO_TRAY_DRAGGING_THUMB_OPACITY : 1}
      previewEnabled={!placement?.isDraggingPhoto}
      onPointerDown={handlePointerDown}
      onActivate={placement ? handleActivate : undefined}
      removeLabel={showRemove ? `Remove ${photo.displayName} from recommendations` : undefined}
      onRemove={onRemove}
      removeDisabled={removeDisabled}
      onPreviewOpenChange={onPreviewOpenChange}
    />
  )
}
